/**
 * aC Men's Hub の選定レビュー API contract（zod schema + 型）。
 * API（apps/api/ac-mens-hub）と Web（apps/web/ac-mens-hub）が共有する。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 選定レビュー機能の API と Web の契約を共有し、一覧・詳細・見立ての型ドリフトを防ぐ。
 * @graph-connects none
 */
import { z } from 'zod';
/**
 * 人が選定を見て付ける見立て、およびお客様が返す総合評価。
 * どちらも「良い / 良くない」の2値で、後から突き合わせて答え合わせに使うため同じ語彙を共有する。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 見立てとお客様評価を同じ2値語彙にそろえ、答え合わせで直接比較できるようにする。
 * @graph-connects none
 */
export const ReviewVerdictSchema = z.enum(['good', 'bad']);
/**
 * お客様の総合評価 `order_review.styling_review` の生値から見立て語彙への対応。
 * 本番実測（2026-08-28）で `'1'` = 良い 468件 / `'0'` = 良くない 124件 の2値しか存在しない。
 * 未回答は行自体が無いので、ここに `null` の項目は置かない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business お客様評価の生値（0/1）を見立てと同じ語彙へ変換する唯一の対応表。
 * @graph-connects none
 */
export const STYLING_REVIEW_RAW_TO_VERDICT = {
    '1': 'good',
    '0': 'bad',
};
/**
 * 着用サイズ感 `item_review[].size_feeling` の生値。1=小さい / 2=ちょうど / 3=大きい。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 着ごとのサイズ感を、生値ではなく意味の分かる語彙で画面に渡す。
 * @graph-connects none
 */
export const SIZE_FEELING_BY_RAW = {
    '1': 'small',
    '2': 'just',
    '3': 'large',
};
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 着ごとのサイズ感の語彙。
 * @graph-connects none
 */
export const SizeFeelingSchema = z.enum(['small', 'just', 'large']);
/**
 * 一覧の1行。毎日の未確認を上から潰すのに要る最小限だけを持ち、
 * 詳細でしか使わない項目（カルテ全項目・服の明細・お客様コメント）は含めない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 選定一覧の1行。未確認を上から順に処理するために必要な項目だけを持つ。
 * @graph-connects none
 */
export const StylingReviewListItemSchema = z.object({
    phoenixOrderId: z.number().int(),
    completedAt: z.string(),
    arthurUserId: z.number().int(),
    /** お客様の氏名（姓 名）。未登録なら null。権限キー `styling-review` の配下でのみ返す。 */
    customerName: z.string().nullable(),
    orderSeq: z.number().int(),
    stylistId: z.number().int().nullable(),
    /** 担当スタイリストの氏名。取得できない場合はID文字列に落ちる。 */
    stylistName: z.string().nullable(),
    height: z.number().nullable(),
    weight: z.number().nullable(),
    planName: z.string().nullable(),
    photoCount: z.number().int(),
    hasRequestText: z.boolean(),
    myVerdict: ReviewVerdictSchema.nullable(),
    isRead: z.boolean(),
    customerVerdict: ReviewVerdictSchema.nullable(),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 選定一覧の応答。未確認件数を添えて、画面が最初に出す「残り何件」を1回の通信で賄う。
 * @graph-connects none
 */
export const StylingReviewListResponseSchema = z.object({
    items: z.array(StylingReviewListItemSchema),
    unreadCount: z.number().int(),
});
/**
 * 一覧の絞り込み。既定は「未確認のみ」で、日付範囲は指定しない（全期間を新しい順）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 選定一覧の絞り込み条件。未確認・答えが返ったもの・見立てと違ったもの・初回を切り替える。
 * @graph-connects none
 */
export const StylingReviewListQuerySchema = z.object({
    filter: z.enum(['all', 'unread', 'answered', 'mismatched', 'first']).default('unread'),
    limit: z.number().int().min(1).max(200).default(50),
});
/**
 * 服の実寸1項目。`specifications` は項目数がアイテムごとに違う（肩幅・身幅・ウエスト・股下など）ため
 * 固定フィールドにせず配列で持つ。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 服の実寸1項目。カテゴリごとに測る箇所が違うため固定フィールドにしない。
 * @graph-connects none
 */
export const ItemSpecificationSchema = z.object({
    code: z.string(),
    name: z.string(),
    value: z.number(),
    unit: z.string(),
});
/**
 * 今回送った服1着。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 送った服1着の見え方（写真・ブランド・サイズ・上代・実寸）をまとめて画面に渡す。
 * @graph-connects none
 */
export const StylingReviewItemSchema = z.object({
    phoenixItemSkuId: z.number().int(),
    itemName: z.string().nullable(),
    brandName: z.string().nullable(),
    categoryName: z.string().nullable(),
    acColor: z.string().nullable(),
    acSize: z.string().nullable(),
    retailPrice: z.number().nullable(),
    imageUrl: z.string().nullable(),
    specifications: z.array(ItemSpecificationSchema),
    isUserPicked: z.boolean(),
});
/**
 * お客様が返した着ごとの評価。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 着ごとのお客様評価。サイズ感・着用有無・色・デザイン・自由記述を持つ。
 * @graph-connects none
 */
export const CustomerItemAnswerSchema = z.object({
    phoenixItemSkuId: z.number().int(),
    sizeFeeling: SizeFeelingSchema.nullable(),
    isUsed: z.boolean().nullable(),
    colorRating: z.number().int().nullable(),
    designRating: z.number().int().nullable(),
    comment: z.string().nullable(),
});
/**
 * 前回までの1回分の感想。件数は最大3回分（本番実測で1人あたり最大4回の注文）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 過去1回分の結果を要約する。今回の選定がその反省を踏まえているかを見るために使う。
 * @graph-connects none
 */
export const PastRoundSchema = z.object({
    phoenixOrderId: z.number().int(),
    orderSeq: z.number().int(),
    completedAt: z.string().nullable(),
    customerVerdict: ReviewVerdictSchema.nullable(),
    itemCount: z.number().int(),
    misfitCount: z.number().int(),
    usedCount: z.number().int(),
    avgColorRating: z.number().nullable(),
    avgDesignRating: z.number().nullable(),
    comments: z.array(z.string()),
});
/**
 * 詳細画面が必要とする1件ぶんの全情報。
 * 画面は「いつ・誰に → どんな要望に → 何を送ったか → 見立て → お客様の答え」の順に読むため、
 * その順序でフィールドを並べる。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 選定1件を判断するのに要る情報を1つの応答にまとめ、画面が複数回の通信をしなくて済むようにする。
 * @graph-connects none
 */
export const StylingReviewDetailSchema = z.object({
    phoenixOrderId: z.number().int(),
    completedAt: z.string(),
    stylistId: z.number().int().nullable(),
    stylistName: z.string().nullable(),
    orderSeq: z.number().int(),
    customer: z.object({
        arthurUserId: z.number().int(),
        /**
         * お客様の登録写真。`arthur.user_images` の `image_type = 3` から取る。
         * カルテ側の `styling_info.image_atmosphere` は**名前に反して同じ写真URLの配列**であり
         * 「雰囲気」を表す値ではない（本番実測で両者は同一URL）。混同しないこと。
         */
        photoUrls: z.array(z.string()),
        /** 氏名（姓 名）。未登録なら null。 */
        name: z.string().nullable(),
        /** 氏名カナ（セイ メイ）。未登録なら null。 */
        nameKana: z.string().nullable(),
        /** 選定時点の年齢。生年月日が未登録なら null。 */
        age: z.number().int().nullable(),
        height: z.number().nullable(),
        weight: z.number().nullable(),
        topsSize: z.string().nullable(),
        pantsSize: z.string().nullable(),
        occupation: z.string().nullable(),
        industry: z.string().nullable(),
        /** なりたい第一印象。お客様は複数選ぶ（本番実測で2つ）ため配列。 */
        firstImpressions: z.array(z.string()),
        officeFashion: z.string().nullable(),
        groupTaste: z.string().nullable(),
        additionalNotes: z.string().nullable(),
        planName: z.string().nullable(),
        planPrice: z.number().nullable(),
    }),
    request: z.object({
        text: z.string().nullable(),
        scenes: z.array(z.number().int()),
        rejectReason: z.string().nullable(),
    }),
    history: z.array(PastRoundSchema),
    items: z.array(StylingReviewItemSchema),
    totalRetailPrice: z.number().nullable(),
    stylistComment: z.string().nullable(),
    myVerdict: ReviewVerdictSchema.nullable(),
    myMemo: z.string().nullable(),
    isRead: z.boolean(),
    customerVerdict: ReviewVerdictSchema.nullable(),
    customerItemAnswers: z.array(CustomerItemAnswerSchema),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 詳細取得の入力。選定は phoenix_order_id で一意に決まる。
 * @graph-connects none
 */
export const StylingReviewDetailQuerySchema = z.object({
    phoenixOrderId: z.number().int(),
});
/**
 * 見立ての保存。見立てを付けずに「読んだ」だけ記録できるよう `verdict` は省略可能にする。
 * メモは任意で、空文字は「メモ無し」として `null` に正規化して保存する。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 見立てと既読の保存入力。見立てを決めずに読了だけ記録する場合も通す。
 * @graph-connects none
 */
export const StylingReviewMarkRequestSchema = z.object({
    phoenixOrderId: z.number().int(),
    verdict: ReviewVerdictSchema.nullable(),
    memo: z.string().max(2000).nullable(),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 見立て保存後の状態。画面はこれで自分の表示を差し替える。
 * @graph-connects none
 */
export const StylingReviewMarkSchema = z.object({
    phoenixOrderId: z.number().int(),
    verdict: ReviewVerdictSchema.nullable(),
    memo: z.string().nullable(),
    isRead: z.boolean(),
    updatedAt: z.string(),
    updatedBy: z.string(),
});
//# sourceMappingURL=styling-review.js.map